interface TrailParticle {
  x: number
  y: number
  size: number
  alpha: number
  decay: number
}

interface Firework {
  x: number
  y: number
  targetX: number
  targetY: number
  color: string
  size: number
  speed: { x: number; y: number }
  done: boolean
  trailParticles: TrailParticle[]
  update: () => void
  draw: (ctx: CanvasRenderingContext2D) => void
}

interface Particle {
  x: number
  y: number
  size: number
  color: string
  alpha: number
  velocity: number
  angle: number
  decay: number
  life: number
  zigzag: number
  connectedTo: number[] | null
  dataValue: number | null
  update: () => void
  draw: (ctx: CanvasRenderingContext2D) => void
}

// Utility function to create fireworks
export const createFirework = (
  startX: number,
  startY: number,
  targetX: number,
  targetY: number,
  color: string,
): Firework => {
  const angle = Math.atan2(targetY - startY, targetX - startX)
  const velocity = 6.5

  return {
    x: startX,
    y: startY,
    targetX,
    targetY,
    color,
    size: 3,
    speed: {
      x: Math.cos(angle) * velocity,
      y: Math.sin(angle) * velocity,
    },
    done: false,
    trailParticles: [],
    update() {
      this.x += this.speed.x
      this.y += this.speed.y

      if (Math.random() > 0.2) {
        this.trailParticles.push({
          x: this.x,
          y: this.y,
          size: Math.random() * 2.5 + 0.5,
          alpha: 0.9,
          decay: 0.02 + Math.random() * 0.02,
        })
      }

      for (let i = this.trailParticles.length - 1; i >= 0; i--) {
        const particle = this.trailParticles[i]
        particle.alpha -= particle.decay
        if (particle.alpha <= 0) {
          this.trailParticles.splice(i, 1)
        }
      }

      const distanceToTarget = Math.hypot(
        this.targetX - this.x,
        this.targetY - this.y,
      )
      if (distanceToTarget < 5) {
        this.done = true
      }
    },
    draw(ctx) {
      for (const particle of this.trailParticles) {
        ctx.save()
        ctx.globalAlpha = particle.alpha
        ctx.fillStyle = this.color
        ctx.beginPath()
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }

      ctx.save()
      ctx.fillStyle = this.color
      ctx.beginPath()
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
      ctx.fill()

      ctx.shadowBlur = 20
      ctx.shadowColor = this.color
      ctx.globalAlpha = 0.8
      ctx.beginPath()
      ctx.arc(this.x, this.y, this.size + 3, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    },
  }
}

// Utility function to create random firework on screen
export const createRandomFirework = (
  canvas: HTMLCanvasElement,
  colors: string[],
): Firework => {
  const startX = canvas.width / 2
  const startY = canvas.height
  const targetX = Math.random() * canvas.width
  const targetY = Math.random() * (canvas.height * 0.6)
  const color = colors[Math.floor(Math.random() * colors.length)]

  return createFirework(startX, startY, targetX, targetY, color)
}

// Utility function to capture canvas as image
export const captureCanvas = (canvas: HTMLCanvasElement): string => {
  return canvas.toDataURL('image/png')
}

// Create effect particles based on style
export const createEffectParticles = (
  x: number,
  y: number,
  colors: string[],
  size: number,
  density: number,
  type: 'burst' | 'digital_rain' | 'neural_network' | 'glitch' | 'data_bloom',
  speed: number,
): Particle[] => {
  const particles: Particle[] = []
  const particleCount = Math.floor(30 + density / 15)
  const angleIncrement = (Math.PI * 2) / particleCount

  for (let i = 0; i < particleCount; i++) {
    const color = colors[Math.floor(Math.random() * colors.length)]
    let angle: number
    let velocity: number
    let life: number
    let particleSize: number

    switch (type) {
      case 'digital_rain':
        angle = -Math.PI / 2 + (Math.random() * 0.4 - 0.2)
        velocity = 2.0 + Math.random() * 3.5
        life = 60 + Math.random() * 60
        particleSize = Math.max(2, (Math.random() * size) / 7)
        break
      case 'neural_network':
        angle = Math.random() * Math.PI * 2
        velocity = 0.8 + Math.random() * 2.8
        life = 80 + Math.random() * 40
        particleSize = Math.max(1.5, (Math.random() * size) / 10)
        break
      case 'glitch':
        angle = Math.random() * Math.PI * 2
        velocity = (1.8 + Math.random() * 5) * (Math.random() > 0.5 ? 1 : -1)
        life = 30 + Math.random() * 25
        particleSize = Math.max(3, (Math.random() * size) / 6)
        break
      case 'data_bloom':
        angle = i * angleIncrement
        velocity = 2.0 + Math.random() * 2.8
        life = 50 + Math.random() * 50
        particleSize = Math.max(2, (Math.random() * size) / 8)
        break
      default:
        angle = i * angleIncrement
        velocity = 2.0 + Math.random() * 4.0
        life = 40 + Math.random() * 50
        particleSize = Math.max(2, (Math.random() * size) / 10)
        break
    }

    particles.push({
      x,
      y,
      size: particleSize,
      color,
      alpha: 1,
      velocity,
      angle,
      decay: 0.015 - Math.random() * 0.005,
      life,
      zigzag: type === 'glitch' ? Math.random() * 4 : 0,
      connectedTo: type === 'neural_network' ? [] : null,
      dataValue: type === 'digital_rain' ? Math.floor(Math.random() * 2) : null,
      update() {
        this.x += Math.cos(this.angle) * this.velocity * speed
        this.y += Math.sin(this.angle) * this.velocity * speed + 0.05
        this.alpha -= this.decay

        if (type === 'glitch' && Math.random() < 0.08) {
          this.x += Math.random() * 10 - 5
          if (Math.random() < 0.04) {
            this.alpha = Math.min(1, this.alpha + 0.2)
          }
        }

        if (type === 'neural_network' && Math.random() < 0.12) {
          this.angle += Math.random() * 0.25 - 0.125
        }

        if (type === 'digital_rain' && Math.random() < 0.12) {
          this.dataValue = Math.floor(Math.random() * 2)
        }

        if (type === 'data_bloom' && Math.random() < 0.03) {
          this.size += 0.4
        }

        if (this.zigzag > 0 && Math.random() < 0.2) {
          this.x += Math.random() * this.zigzag - this.zigzag / 2
        }
      },
      draw(ctx) {
        ctx.save()
        ctx.globalAlpha = this.alpha
        ctx.fillStyle = this.color

        if (type === 'glitch') {
          const glitchHeight = this.size / (Math.random() > 0.8 ? 1 : 2)
          ctx.fillRect(
            this.x - this.size / 2,
            this.y - glitchHeight / 2,
            this.size * (Math.random() > 0.95 ? 1.5 : 1),
            glitchHeight,
          )

          if (Math.random() < 0.08) {
            ctx.fillRect(
              this.x + (Math.random() * 8 - 4),
              this.y + (Math.random() * 8 - 4),
              Math.random() * 2,
              Math.random() * 2,
            )
          }
        } else if (type === 'data_bloom') {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
          ctx.fill()

          ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
          ctx.lineWidth = 0.5
          ctx.stroke()

          if (Math.random() < 0.15) {
            ctx.beginPath()
            ctx.arc(
              this.x + (Math.random() * 8 - 4),
              this.y + (Math.random() * 8 - 4),
              1,
              0,
              Math.PI * 2,
            )
            ctx.fill()
          }
        } else if (type === 'digital_rain') {
          ctx.font = `${this.size * 2.5}px monospace`
          ctx.fillText(this.dataValue!.toString(), this.x, this.y)

          if (Math.random() < 0.04) {
            ctx.globalAlpha = this.alpha * 0.3
            for (let i = 1; i < 4; i++) {
              ctx.fillText(
                Math.floor(Math.random() * 2).toString(),
                this.x,
                this.y + i * this.size * 2,
              )
            }
          }
        } else if (type === 'neural_network') {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
          ctx.fill()

          ctx.strokeStyle = this.color
          ctx.lineWidth = 0.5
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size * 1.4, 0, Math.PI * 2)
          ctx.globalAlpha = this.alpha * 0.3
          ctx.stroke()
        } else {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
          ctx.fill()

          if (Math.random() < 0.08) {
            ctx.globalAlpha = this.alpha * 0.3
            ctx.beginPath()
            ctx.moveTo(this.x, this.y)
            const trailLength = this.size * 3
            ctx.lineTo(
              this.x - Math.cos(this.angle) * trailLength,
              this.y - Math.sin(this.angle) * trailLength,
            )
            ctx.strokeStyle = this.color
            ctx.lineWidth = this.size / 2
            ctx.stroke()
          }
        }

        ctx.shadowBlur = 12
        ctx.shadowColor = this.color
        ctx.globalAlpha = this.alpha * 0.7

        if (type === 'glitch') {
          const glitchHeight = this.size / (Math.random() > 0.8 ? 1 : 2)
          ctx.fillRect(
            this.x - this.size / 2,
            this.y - glitchHeight / 2,
            this.size * (Math.random() > 0.95 ? 1.5 : 1),
            glitchHeight,
          )
        } else if (type === 'data_bloom') {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size + 2, 0, Math.PI * 2)
          ctx.fill()
        } else if (type === 'digital_rain') {
          ctx.font = `${this.size * 2.5}px monospace`
          ctx.fillText(this.dataValue!.toString(), this.x, this.y)
        } else {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size + 2, 0, Math.PI * 2)
          ctx.fill()
        }
        ctx.restore()
      },
    })
  }

  if (type === 'neural_network') {
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const p1 = particles[i]
        const p2 = particles[j]
        const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y)
        if (distance < 50 && p1.connectedTo && p2.connectedTo) {
          p1.connectedTo.push(j)
          p2.connectedTo.push(i)
        }
      }
    }

    for (const particle of particles) {
      const originalDraw = particle.draw
      particle.draw = function (ctx) {
        originalDraw.call(this, ctx)

        if (
          this.connectedTo &&
          this.connectedTo.length > 0 &&
          this.alpha > 0.2
        ) {
          ctx.save()
          ctx.strokeStyle = this.color
          ctx.globalAlpha = this.alpha * 0.4
          ctx.lineWidth = 0.6

          for (const index of this.connectedTo) {
            const connectedParticle = particles[index]
            if (connectedParticle && connectedParticle.alpha > 0.2) {
              const gradient = ctx.createLinearGradient(
                this.x,
                this.y,
                connectedParticle.x,
                connectedParticle.y,
              )

              const time = Date.now() / 1000
              const animationProgress = (Math.sin(time * 2) + 1) / 2

              gradient.addColorStop(0, this.color)
              gradient.addColorStop(
                animationProgress,
                'rgba(255, 255, 255, 0.8)',
              )
              gradient.addColorStop(1, connectedParticle.color)

              ctx.strokeStyle = gradient

              ctx.beginPath()
              ctx.moveTo(this.x, this.y)
              ctx.lineTo(connectedParticle.x, connectedParticle.y)
              ctx.stroke()

              if (Math.random() < 0.05) {
                const packetPos = Math.random()
                const packetX =
                  this.x + (connectedParticle.x - this.x) * packetPos
                const packetY =
                  this.y + (connectedParticle.y - this.y) * packetPos

                ctx.beginPath()
                ctx.arc(packetX, packetY, 1.5, 0, Math.PI * 2)
                ctx.fillStyle = 'white'
                ctx.fill()
              }
            }
          }
          ctx.restore()
        }
      }
    }
  }

  return particles
}
